import clsx from 'clsx';
import React, { useEffect, useId, useRef, useState } from 'react';
import { makePath } from 'utils/routes';
import { Link, useLocation } from 'wouter';

const NAV_ICON_SIZE = 14;
const DRIFT_DUR = '3s';

type NavIconProps = { active: boolean; hovered: boolean };

/** Shared animated radial gradient for active/hovered nav icons */
const NavAnimatedGradient = ({
  id,
  cx,
  cy,
  r = '70%',
  animate,
}: {
  id: string;
  cx: string;
  cy: string;
  r?: string;
  animate: boolean;
}) => {
  const cxRef = useRef<SVGAnimateElement>(null);
  const cyRef = useRef<SVGAnimateElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!animate) return undefined;
    const randPct = () => `${15 + Math.floor(Math.random() * 70)}%`;
    const kick = () => {
      [cxRef, cyRef].forEach((ref) => {
        const anim = ref.current;
        if (!anim) return;
        const start = anim.getAttribute('data-start') ?? '50%';
        const mid = randPct();
        anim.setAttribute('values', `${start};${mid};${start}`);
        anim.beginElement();
      });
    };
    kick();
    intervalRef.current = setInterval(kick, 3000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [animate]);

  const color0 = animate
    ? 'rgb(var(--colour-main-800))'
    : 'rgb(var(--colour-main-300))';
  const color1 = animate
    ? 'rgb(var(--colour-main))'
    : 'rgb(var(--colour-main-200))';

  return (
    <radialGradient id={id} cx={cx} cy={cy} r={r}>
      <stop offset="0%" stopColor={color0} />
      <stop offset="100%" stopColor={color1} />
      <animate
        ref={cxRef}
        attributeName="cx"
        data-start={cx}
        values={`${cx};${cx};${cx}`}
        dur={DRIFT_DUR}
        begin="indefinite"
        fill="freeze"
      />
      <animate
        ref={cyRef}
        attributeName="cy"
        data-start={cy}
        values={`${cy};${cy};${cy}`}
        dur={DRIFT_DUR}
        begin="indefinite"
        fill="freeze"
      />
    </radialGradient>
  );
};

/** Document icon matching DynamicInvoiceIcon shape */
const NavDocumentIcon = ({ active, hovered }: NavIconProps) => {
  const id = useId();
  const width = NAV_ICON_SIZE * (18 / 24);
  const animate = active || hovered;
  return (
    <svg
      width={width}
      height={NAV_ICON_SIZE}
      viewBox="0 0 18 24"
      fill="none"
      className="shrink-0"
    >
      <defs>
        <NavAnimatedGradient
          id={`${id}-rg`}
          cx="40%"
          cy="60%"
          animate={animate}
        />
      </defs>
      <path
        d="M11.5 6C11.5 6.27614 11.7239 6.5 12 6.5H18V23.5C18 23.7761 17.7761 24 17.5 24H0.5C0.223858 24 8.05326e-09 23.7761 0 23.5V0.5C0 0.223858 0.223858 8.05324e-09 0.5 0H11.5V6Z"
        fill={`url(#${id}-rg)`}
      />
      <path
        d="M12.25 0.301777V5.625C12.25 5.69404 12.306 5.75 12.375 5.75L17.6982 5.75C17.8096 5.75 17.8654 5.61536 17.7866 5.53661L12.4634 0.213388C12.3846 0.134643 12.25 0.190413 12.25 0.301777Z"
        fill={`url(#${id}-rg)`}
        opacity={active ? 0.8 : 1}
      />
    </svg>
  );
};

/** Circle icon matching UserColorDot shape */
const NavCircleIcon = ({ active, hovered }: NavIconProps) => {
  const id = useId();
  const animate = active || hovered;
  return (
    <svg
      width={NAV_ICON_SIZE}
      height={NAV_ICON_SIZE}
      viewBox={`0 0 ${NAV_ICON_SIZE} ${NAV_ICON_SIZE}`}
      className="shrink-0"
    >
      <defs>
        <NavAnimatedGradient
          id={`${id}-rg`}
          cx="45%"
          cy="55%"
          r="60%"
          animate={animate}
        />
      </defs>
      <circle
        cx={NAV_ICON_SIZE / 2}
        cy={NAV_ICON_SIZE / 2}
        r={NAV_ICON_SIZE / 2}
        fill={`url(#${id}-rg)`}
      />
    </svg>
  );
};

/** Rounded square icon matching ItemColorSquare shape (1px smaller than other icons) */
const NAV_SQUARE_SIZE = NAV_ICON_SIZE - 1;
const NavSquareIcon = ({ active, hovered }: NavIconProps) => {
  const id = useId();
  const animate = active || hovered;
  return (
    <svg
      width={NAV_SQUARE_SIZE}
      height={NAV_SQUARE_SIZE}
      viewBox={`0 0 ${NAV_SQUARE_SIZE} ${NAV_SQUARE_SIZE}`}
      className="shrink-0"
    >
      <defs>
        <NavAnimatedGradient
          id={`${id}-rg`}
          cx="55%"
          cy="45%"
          animate={animate}
        />
      </defs>
      <rect
        width={NAV_SQUARE_SIZE}
        height={NAV_SQUARE_SIZE}
        rx={2}
        ry={2}
        fill={`url(#${id}-rg)`}
      />
    </svg>
  );
};

/** Isometric triangle icon for Recon */
export const NavTriangleIcon = ({ active, hovered }: NavIconProps) => {
  const id = useId();
  const h = NAV_ICON_SIZE;
  const w = NAV_ICON_SIZE;
  const points = `${w / 2},1 ${w - 1},${h - 1} 1,${h - 1}`;
  const animate = active || hovered;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
      <defs>
        <NavAnimatedGradient
          id={`${id}-rg`}
          cx="50%"
          cy="55%"
          r="65%"
          animate={animate}
        />
      </defs>
      <polygon
        points={points}
        fill={`url(#${id}-rg)`}
        stroke={`url(#${id}-rg)`}
        strokeWidth={1}
        strokeLinejoin="round"
      />
    </svg>
  );
};

type NavItemProps = {
  href: string;
  label: string;
  active: boolean;
  renderIcon: (props: NavIconProps) => React.ReactNode;
  isCollapsed?: boolean;
};

const NavItem = ({
  href,
  label,
  active,
  renderIcon,
  isCollapsed,
}: NavItemProps) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link href={href}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={clsx(
          'group/item flex items-center border-b-[0.5px] border-main-200 text-xmd font-helvetica pl-[calc(0.5lh+3.5px)] h-f4',
          isCollapsed ? '' : 'gap-x-f1 pr-f1 w-full',
          active ? 'text-main bg-main-100' : 'text-main-500 hover:text-main'
        )}
        data-testid={`${label}-nav-item`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span className="flex items-center justify-center shrink-0 w-[14px]">
          {renderIcon({ active, hovered })}
        </span>
        {!isCollapsed && <span className="truncate">{label}</span>}
      </a>
    </Link>
  );
};

type PaymentsNavProps = {
  workspaceId: string;
  isCollapsed?: boolean;
};

export const PaymentsNav = ({ workspaceId, isCollapsed }: PaymentsNavProps) => {
  const [location] = useLocation();

  const isInvoicesActive = location.includes('/invoices');
  const isReconActive = location.includes('/transactions');
  const isCatalogActive = location.includes('/catalog');
  const isUsersActive = location.includes('/users');

  return (
    <div
      className={clsx('flex flex-col', !isCollapsed && '-mx-f1')}
      data-testid="payments-nav"
    >
      <NavItem
        href={makePath(['w', workspaceId, 'payments', 'invoices'])}
        label="Invoices"
        active={isInvoicesActive}
        renderIcon={({ active, hovered }) => (
          <NavDocumentIcon active={active} hovered={hovered} />
        )}
        isCollapsed={isCollapsed}
      />
      <NavItem
        href={makePath(['w', workspaceId, 'payments', 'transactions'])}
        label="Reconciliation"
        active={isReconActive}
        renderIcon={({ active, hovered }) => (
          <NavTriangleIcon active={active} hovered={hovered} />
        )}
        isCollapsed={isCollapsed}
      />
      <NavItem
        href={makePath(['w', workspaceId, 'payments', 'catalog'])}
        label="Catalog"
        active={isCatalogActive}
        renderIcon={({ active, hovered }) => (
          <NavSquareIcon active={active} hovered={hovered} />
        )}
        isCollapsed={isCollapsed}
      />
      <NavItem
        href={makePath(['w', workspaceId, 'payments', 'users'])}
        label="Users"
        active={isUsersActive}
        renderIcon={({ active, hovered }) => (
          <NavCircleIcon active={active} hovered={hovered} />
        )}
        isCollapsed={isCollapsed}
      />
    </div>
  );
};
